import { Injectable, Signal, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { Charity, CharityFilterRequest } from '../../models/Charity';
import { HttpClientWrapperService, Params } from '../http/http-client-wrapper.service';
import { CharityManagementService } from './charity-management.service';
import { AuthService } from '../auth/auth.service';
import { OrgContextStorageService } from '../auth/org-context-storage.service';
import { CharityDonor } from '../../models/User';
import { ApiUrlService } from '../api/api-url.service';

@Injectable({
  providedIn: 'root',
})
export class CharityService {
  private readonly apiUrlService = inject(ApiUrlService);
  private readonly charityManagementService = inject(CharityManagementService);
  private readonly authService = inject(AuthService);
  private readonly orgContextStorage = inject(OrgContextStorageService);
  private get baseApiUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/charities`); }

  private readonly _currentCharity = signal<Charity | null>(null);
  readonly currentCharity: Signal<Charity | null> = this._currentCharity.asReadonly();
  readonly charity$ = toObservable(this._currentCharity);

  constructor(private httpClient: HttpClientWrapperService) {
    this.initializeCharity();
    this.setupUserListener();
  }

  private setupUserListener(): void {
    // user$ replays its current value (null) synchronously at boot; clearing on
    // that initial emission deletes the sessionStorage key initializeCharity()
    // just restored. Only clear when a logged-in user actually logs out.
    let hasSeenUser: boolean = false;
    this.authService.user$.subscribe(user => {
      if (user) {
        hasSeenUser = true;
        return;
      }
      if (hasSeenUser) {
        hasSeenUser = false;
        this.clearCurrentCharity();
      }
    });
  }

  private initializeCharity(): void {
    const charityId: string | null = sessionStorage.getItem('currentCharityId');
    if (charityId) {
      this.setCurrentCharity(charityId, false);
    }
  }

  getCharities(
    query: string = '',
    charitySegment: string = '',
    state: string = '',
    offset: number = 0,
    limit: number = 0,
    sort: string[] = [],
  ): Observable<Charity[]> {
    const args: CharityFilterRequest = { offset };

    if (charitySegment) {
      args.charitySegment = charitySegment;
    }

    if (state) {
      args.state = state;
    }

    if (query) {
      args.q = query;
    }

    if (limit) {
      args.limit = limit;
    }

    if (sort.length > 0) {
      args.sort = sort;
    }

    return this.httpClient.get<Charity[]>(`${this.baseApiUrl}`, args);
  }

  setCurrentCharity(id: string, persist: boolean = true): void {
    this.httpClient.get<Charity>(`${this.baseApiUrl}/${id}`).subscribe({
      next: (res: Charity) => {
        this._currentCharity.set(res);
        if (persist) {
          sessionStorage.setItem('currentCharityId', id);
          try { this.orgContextStorage.setLastUsedOrgId('charity', id); } catch { /* persistence is best-effort */ }
        }
        this.charityManagementService.loadCurrentCharityUser(id);
      },
      error: () => {
        this.clearCurrentCharity();
      }
    });
  }

  clearCurrentCharity(): void {
    this._currentCharity.set(null);
    sessionStorage.removeItem('currentCharityId');
    this.charityManagementService.clearCurrentCharityUser();
  }

  getCharity(id: string): Observable<Charity> {
    return this.httpClient.get<Charity>(`${this.baseApiUrl}/${id}`);
  }

  getThinCharity(id: string): Observable<Charity> {
    return this.httpClient.get<Charity>(`${this.baseApiUrl}/thin/${id}`);
  }

  getThinCharitiesRecommended(): Observable<Charity[]> {
    return this.httpClient.get<Charity[]>(`${this.baseApiUrl}/thin/recommended`);
  }

  getThinCharities(
    query: string = '',
    charitySegment: string = '',
    state: string = '',
    offset: number = 0,
    limit: number = 0,
    sort: string[] = [],
  ): Observable<Charity[]> {
    const args: CharityFilterRequest = { offset };

    if (charitySegment) {
      args.charitySegment = charitySegment;
    }

    if (state) {
      args.state = state;
    }

    if (query) {
      args.q = query;
    }

    if (limit) {
      args.limit = limit;
    }

    if (sort.length > 0) {
      args.sort = sort;
    }

    return this.httpClient.get<Charity[]>(`${this.baseApiUrl}/thin`, args);
  }

  getCharityByEin(ein: string): Observable<Charity> {
    return this.httpClient.get<Charity>(`${this.baseApiUrl}/ein/${ein}`);
  }

  updateCharity(id: string, charity: Charity): Observable<Charity> {
    return this.httpClient.put<Charity, Charity>(`${this.baseApiUrl}/${id}`, charity);
  }

  changeCharityProfilePic(id: number, formData: FormData): Observable<Charity> {
    return this.httpClient.post<Charity, FormData>(
      `${this.baseApiUrl}/${id}/upload-image`,
      formData,
    );
  }

  verifyCharityEmail(charityId: number, token: string): Observable<void> {
    return this.httpClient.postWithoutAuth<void, Params>(`${this.baseApiUrl}/verify-email`, {
      relatedId: charityId,
      token,
    });
  }

  getCharityDonors(charityId: string, limit: number = 10): Observable<CharityDonor[]> {
    return this.httpClient.get<CharityDonor[]>(`${this.baseApiUrl}/${charityId}/donors?limit=${limit}`);
  }

  refreshCurrentCharity(): void {
    const charityId: string | null = sessionStorage.getItem('currentCharityId');
    if (charityId) {
      this.setCurrentCharity(charityId, false);
    }
  }
}
